import { makeStyles } from "@material-ui/core";
import * as React from "react";

import { AgentCompatibilityRadarScores } from "@/api/generated";
import { fontFamilies } from "@/designTokens";
import theme from "@/theme";

const useStyles = makeStyles(() => ({
  root: { width: "100%", display: "flex", flexDirection: "column", alignItems: "center" },
  canvas: { display: "block", width: 294, maxWidth: "100%", height: "auto", overflow: "visible" },
  label: { fill: theme.palette.semantic["text/secondary"], fontFamily: fontFamilies.body, fontSize: 13.569 },
  legend: { display: "flex", justifyContent: "center", gap: 20, flexWrap: "wrap", fontSize: 12, lineHeight: 1.5 },
  legendItem: { display: "inline-flex", alignItems: "center", gap: 4 },
  swatch: { width: 16, height: 16, borderRadius: 4, display: "inline-block", flexShrink: 0 },
  baseline: { backgroundColor: theme.palette.semantic["chart/baseline"] },
  series: { border: `1.5px solid ${theme.palette.semantic["action/primary"]}` },
}));

// 静的な基準枠はFigmaの原寸素材。青線だけをAPIの5軸スコアから投影する。
const GRID_LAYERS = [
  { file: "radar_grid_1.svg", x: 130.513, y: 100.294, width: 32.9733, height: 31.4015 },
  { file: "radar_grid_2.svg", x: 114.603, y: 83.567, width: 64.7932, height: 61.6835 },
  { file: "radar_grid_3.svg", x: 98.687, y: 66.827, width: 96.6356, height: 91.9542 },
  { file: "radar_grid_4.svg", x: 82.77, y: 50.091, width: 128.455, height: 122.225 },
  { file: "radar_grid_5.svg", x: 66.847, y: 33.36, width: 160.298, height: 152.507 },
  { file: "radar_spokes.svg", x: 67.245, y: 33.92, width: 159.517, height: 151.708 },
  { file: "radar_baseline.svg", x: 98.687, y: 66.827, width: 96.6356, height: 91.9542 },
];
const LABEL_POSITIONS = [[147.36, 5.65], [244.68, 75.76], [207.36, 189.97], [87.43, 189.97], [49.9, 75.76]];

export const FeaturedCompatibilityRadar: React.FC<{
  scores: AgentCompatibilityRadarScores;
  labels: { key: keyof AgentCompatibilityRadarScores; label: string }[];
  hideLegend: boolean;
}> = ({ scores, labels, hideLegend }) => {
  const classes = useStyles();
  const points = labels.map(({ key }, index) => {
    const value = scores[key];
    const radius = 83.68 * (typeof value === "number" && Number.isFinite(value) ? Math.max(0, Math.min(5, value)) / 5 : 0);
    const angle = (-90 + index * 72) * Math.PI / 180;
    return `${147 + Math.cos(angle) * radius},${117.6 + Math.sin(angle) * radius}`;
  }).join(" ");
  return (
    <div className={classes.root}>
      <svg className={classes.canvas} viewBox="0 0 294 226.154" width="294" height="226.154" role="img" aria-label="物件種別・年収・資産・ステージ・エリアの相性グラフ">
        {GRID_LAYERS.map(({ file, ...geometry }) => <image key={file} href={`/icons/planner_onboarding/${file}`} {...geometry} />)}
        <polygon points={points} fill="none" stroke={theme.palette.semantic["action/primary"]} strokeWidth={2} data-testid="planner-radar-series" />
        {labels.map(({ label }, index) => (
          <text key={label} x={LABEL_POSITIONS[index][0]} y={LABEL_POSITIONS[index][1] + 15} textAnchor="middle" className={classes.label}>{label}</text>
        ))}
      </svg>
      {!hideLegend && (
        <div className={classes.legend}>
          <span className={classes.legendItem}><i className={`${classes.swatch} ${classes.baseline}`} />相性基準ライン</span>
          <span className={classes.legendItem}><i className={`${classes.swatch} ${classes.series}`} />このプランナーとの相性</span>
        </div>
      )}
    </div>
  );
};
